import React, {
  FC,
  useEffect,
  memo,
  useCallback,
  useState,
  useRef,
} from 'react'
import cn from 'classnames'

import Button from '../../components/Button'
import Select from '../../components/Select'
import Input from '../../components/Input'
import { IProduct, IProductTag } from '../../types/products'
import { IAccount, IBranch, IDivision } from '../../types/user'
import { IHash } from '../../types/common'
import Toggle from '../../components/Toggle'
import GearSmall from '../../icons/gear_small.svg'
import { useRepackingModal } from './RepackingModal'
import Transfer from '../../icons/transfer.svg'
import ProductTransfer from './ProductTransfer'
import Tooltip from '../../components/Tooltip'
import { DirectUpload } from '@rails/activestorage'
import CircleMinus from '../../icons/circle-minus.svg'
import Loading from '../../icons/loading.svg'
import EditAmountModal from './EditAmountModal'
import TagDropdown from './TagDropdown'
import { money } from '../../common/display'

interface IProductFormProps {
  product: IProduct
  account?: IAccount
  divisions: IDivision[]
  distributors: string[]
  productTags: IProductTag[]
  branch: IBranch
  loading: boolean
  errors: IHash<string>
  binInventoryFeature?: boolean
  onSave: (product: IProduct) => void
  onRefetch: () => void
  onRemove: (product: IProduct) => void
  onCancel: () => void
}

const ProductForm: FC<IProductFormProps> = ({
  product: initialProduct,
  branch,
  account,
  distributors,
  productTags,
  loading,
  errors,
  binInventoryFeature,
  onSave,
  onRefetch,
  onRemove,
  onCancel,
}) => {
  const [photoLoading, setPhotoLoading] = useState(false)

  const [product, setProduct] = useState<IProduct>({ ...initialProduct })
  const [price, setPrice] = useState<string>(
    (initialProduct.price || '').toString(),
  )
  const [moveVisible, setMoveVisible] = useState<boolean>(false)

  useEffect(() => {
    setProduct({ ...initialProduct })
    setPrice((initialProduct.price || '').toString())
    if (initialProduct.isAddingToBranch) {
      setTimeout(() => stockInputRef.current?.focus(), 0)
    }
  }, [initialProduct])

  const isCustom = product.custom && !product.originalProductData
  const canUploadPhoto = isCustom && !product.isAddingToBranch

  const handleNotifyThresholdChange = useCallback(
    (notifyThreshold) => setProduct({ ...product, notifyThreshold }),
    [product],
  )

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault()
      setProduct({ ...product })

      onSave({ ...product })
    },
    [product],
  )
  const distributorsOptions = distributors.map((d) => ({ label: d, value: d }))
  const isNew = !product.id
  const isInitialSetup = isNew || !!product.isAddingToBranch

  const handleChangeRepacking = (p: IProduct) => {
    setProduct({ ...p })
    onSave({ ...p })
  }

  const [editAmountVisible, setEditAmountVisible] = useState(false)

  const handleEditAmount = (amount: number, reason: string) => {
    const updated = { ...product, inventory: amount, reason }
    setProduct(updated)
    onSave(updated)
    setEditAmountVisible(false)
  }

  const handleChangeCurrentStock = (product: IProduct, value: string) => {
    const newValue = parseInt(value, 10)
    const inventory = !!newValue || newValue === 0 ? newValue : undefined

    setProduct({ ...product, inventory })
  }

  const [{ open: handleRepackingModal }, repackingModal] = useRepackingModal({
    product,
    errors,
    onChange: handleChangeRepacking,
  })

  const handleUpload = async (file: File) => {
    const upload = new DirectUpload(
      file,
      '/rails/active_storage/direct_uploads',
    )
    setPhotoLoading(true)

    upload.create((error: Error, blob: any) => {
      if (error) {
        setPhotoLoading(false)
      } else {
        const reader = new FileReader()
        reader.onloadend = () => {
          setProduct({
            ...product,
            photo: reader.result as string,
            blobSignedId: blob!.signed_id,
            deletePhoto: undefined,
          })
          setPhotoLoading(false)
        }
        reader.readAsDataURL(file)
      }
    })
  }

  const handleDelete = (event: React.MouseEvent<SVGSVGElement>) => {
    event.stopPropagation()
    setProduct({
      ...product,
      photo: undefined,
      blobSignedId: undefined,
      deletePhoto: true,
    })
  }

  const fileInputRef = useRef<HTMLInputElement>(null)
  const stockInputRef = useRef<HTMLInputElement>(null)

  const handleClick = () => {
    if (photoLoading) {
      return
    }
    fileInputRef.current?.click()
  }

  const showReadOnlyStock = !isInitialSetup && !!binInventoryFeature
  const showEditAmountButton = !isInitialSetup && !binInventoryFeature

  const getStockError = () => {
    if (errors.inventory) return errors.inventory
    if (typeof product.inventory === 'number' && product.inventory < 0) {
      return 'Must be greater than or equal to 0'
    }
  }

  const currentStockField = (
    <>
      {isInitialSetup && (
        <Input
          ref={stockInputRef}
          label="Current stock"
          error={getStockError()}
          value={product.inventory ?? ''}
          onChange={(e) => handleChangeCurrentStock(product, e.target.value)}
        />
      )}
      {showReadOnlyStock && (
        <Input
          label="Current stock"
          error={errors.inventory}
          value={product.inventory ?? ''}
          readOnly
        />
      )}
      {showEditAmountButton && (
        <div className="product-info_edit-amount">
          <div className="input_label">CURRENT STOCK</div>
          <button
            type="button"
            className="product-info_locations-inventory-edit"
            onClick={() => setEditAmountVisible(true)}
          >
            {product.inventory ?? '—'}
          </button>
          {errors.inventory && (
            <div className="input_error">{errors.inventory}</div>
          )}
        </div>
      )}
    </>
  )

  const transferButton = (
    <Tooltip id="tooltip-transfer">
      <div
        className="input_label-tooltip"
        data-tip="Transfer to another branch"
        data-for="tooltip-transfer"
        data-effect="solid"
        data-place="bottom"
      >
        <Button
          type="button"
          caliber="sm"
          disabled={isInitialSetup}
          className="list_action"
          onClick={() => setMoveVisible(true)}
        >
          <Transfer />
        </Button>
      </div>
    </Tooltip>
  )

  return (
    <form onSubmit={handleSubmit}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg, image/png"
        multiple={false}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) {
            handleUpload(file)
          }
        }}
      />

      {canUploadPhoto && !product.photo && !photoLoading && (
        <div className="product-upload_area" onClick={handleClick}>
          <div className="product-photo_text">
            <div className="bold">UPLOAD IMAGE</div>
            .jpg or .png files,
            <br />
            recommended min.
            <br />
            size is 100x100 px
          </div>
        </div>
      )}

      {canUploadPhoto && product.photo && !photoLoading && (
        <div className="product-photo" onClick={handleClick}>
          <CircleMinus className="delete" onClick={handleDelete} />
          <img src={product.photo} alt={product.name} />
        </div>
      )}

      {canUploadPhoto && photoLoading && (
        <div className="product-photo">
          <Loading />
        </div>
      )}

      <div className="product-info_details">
        <div className={cn('product-info_item', { '-sku': isCustom })}>
          {isCustom ? (
            <Input
              label="Product Id (Sku)"
              error={errors.code}
              value={product.code}
              disabled={!isNew}
              onChange={(e) => setProduct({ ...product, code: e.target.value })}
            />
          ) : (
            <>
              <div className="product-info_label">Product Id (Sku)</div>
              <div className="product-info_value">{product.code}</div>
            </>
          )}
        </div>
        <div className={cn('product-info_item', { '-name': isCustom })}>
          {isCustom ? (
            <Input
              label="Name"
              error={errors.name}
              value={product.name}
              onChange={(e) => setProduct({ ...product, name: e.target.value })}
            />
          ) : (
            <>
              <div className="product-info_label">Name</div>
              <div className="product-info_value">{product.name}</div>
            </>
          )}
        </div>
        <div className="product-info_item -full-width">
          {isCustom ? (
            <Select
              creatable
              isClearable
              label="Distributor Name"
              value={
                product.distributor
                  ? distributorsOptions.find(
                      (d) => d.value === product.distributor,
                    )
                  : undefined
              }
              options={distributorsOptions}
              onChange={(value: any) => {
                setProduct({ ...product, distributor: value && value.value })
              }}
            />
          ) : (
            <>
              <div className="product-info_label">Distributed By</div>
              <div className="product-info_value">
                {(isCustom && (product.distributor || 'n/a')) || 'Forshaw'}
              </div>
            </>
          )}
        </div>
      </div>
      {isCustom && (
        <div className="product-info_repacking -horizontal">
          <div className="product-info_pricing">
            <Input
              label="Unit price, $"
              error={errors.price}
              value={price}
              onKeyPress={(evt) => {
                const charCode = evt.which ? evt.which : evt.keyCode
                const value = (price || '').toString()
                const dotcontains = value.indexOf('.') !== -1
                if (dotcontains)
                  if (charCode === 46) return evt.preventDefault()
                if (charCode === 46) return true
                if (charCode > 31 && (charCode < 48 || charCode > 57))
                  return evt.preventDefault()
                return true
              }}
              onChange={(e) => {
                const value = e.target.value
                setPrice(value)
                setProduct({ ...product, price: +value })
              }}
            />
          </div>

          <div className="product-info_pricing">
            <Input
              label="Unit of measure"
              error={errors.unit}
              value={product.unit}
              onChange={(e) => setProduct({ ...product, unit: e.target.value })}
            />
          </div>

          <div className="product-info_pricing -with-transfer">
            {currentStockField}
            {transferButton}
          </div>
        </div>
      )}
      {!isCustom && (
        <div>
          <div className="product-info_current product-info_pricing -with-transfer">
            {currentStockField}
            {transferButton}
          </div>
          <div className="product-info_repacking_details">
            <div className="product-info_repacking_lines">
              {product.quantity !== undefined && product.quantity > 1 && (
                <div className="product-info_repacking_result">
                  Repacked: {money(product.price ?? 0)} / {product.unit}
                </div>
              )}
              {product.originalProductData ? (
                <div className="product-info_repacking_source">
                  Shipped: {money(product.originalProductData.price ?? 0)} /{' '}
                  {product.originalProductData.unit}
                </div>
              ) : (
                <div className="product-info_repacking_source">
                  Shipped: {money(product.price ?? 0)} / {product.unit}
                </div>
              )}
            </div>
            <div
              className="product-info_repacking_link"
              onClick={() => handleRepackingModal()}
            >
              <GearSmall />
              Repackaging settings
            </div>
          </div>
          {repackingModal}
        </div>
      )}
      {!isNew && (
        <>
          <div className="product-info_box">
            <Toggle
              value={product.notifyThreshold}
              onChange={() =>
                handleNotifyThresholdChange(!product.notifyThreshold)
              }
              option={{
                value: true,
                label: 'Notify me when low stock',
              }}
            />

            {product.notifyThreshold && (
              <div style={{ width: '100%' }}>
                <Input
                  label="Threshold value"
                  value={product.threshold}
                  error={errors.threshold}
                  onChange={(e) => {
                    const threshold = parseInt(
                      e.target.value.replace(/\D/g, '') || '0',
                      10,
                    )
                    setProduct({ ...product, threshold })
                  }}
                />
              </div>
            )}
          </div>
          <br />
        </>
      )}
      <div className="product-info_item -full-width">
        <TagDropdown
          tags={productTags}
          selectedTagId={product.productTags?.[0]?.id}
          selectedPendingTag={product.pendingNewTag}
          onPendingCreate={(pendingTag) => {
            setProduct({
              ...product,
              productTagIds: undefined,
              productTags: [],
              pendingNewTag: pendingTag,
            })
          }}
          onChange={(tag) => {
            setProduct({
              ...product,
              productTagIds: tag != null ? [Number(tag.id)] : [],
              productTags: tag ? [tag] : [],
              pendingNewTag: undefined,
            })
          }}
        />
      </div>

      <div className="product-info_section -footer">
        <Button
          color="success"
          type="submit"
          disabled={loading}
          loading={loading}
        >
          Save
        </Button>
        <div className="product-info_cancel" onClick={onCancel}>
          Cancel
        </div>
        {!isNew && (
          <div
            className="product-info_archive"
            onClick={() => !loading && onRemove(product)}
          >
            Remove Item
          </div>
        )}
      </div>

      <ProductTransfer
        account={account}
        branch={branch}
        opened={moveVisible}
        product={product}
        binInventoryFeature={binInventoryFeature}
        onRefetch={onRefetch}
        onClose={() => setMoveVisible(false)}
      />

      <EditAmountModal
        visible={editAmountVisible}
        currentAmount={product.inventory}
        onSave={handleEditAmount}
        onClose={() => setEditAmountVisible(false)}
      />
    </form>
  )
}

export default memo(ProductForm)
